/**
 * Takes a value and a low and high bound and returns true if value is in the range of the supplied bounds.
 * Order of the bounds sent in does not matter, for example inRange(5, 1, 10) and inRange(5, 10, 1) will
 * both yield true since 5 is between 1-10.
 * @param value
 * @param bound1
 * @param bound2
 */
export function inRange(value: number, bound1: number, bound2: number): boolean {
    return (value - bound1) * (value - bound2) <= 0;
}

/**
 * Takes a `value` and returns true if `Number(value)` yields a finite number.
 *
 * This is equal to `Number.isFinite(value: number)` without the need to parse
 * the value into a number, or equal to the global function `isFinite(value:
 * number)` without the need to type assert `as any` and does not require we
 * disable the eslint rule preventing global functions.
 * @param value
 */
export function isValidNumber(value: number | string | undefined): boolean {
    return Number.isFinite(Number(value));
}

/**
 * Takes a `value` and returns true if `Number(value)` yields a valid integer.
 */
export function isValidInteger(value: number | string | undefined): boolean {
    return Number.isSafeInteger(Number(value));
}

/**
 * Rounds a floating point number to at most some number of decimal places.
 * @param num The number to round.
 * @param decimalPlaces (optional) The maximum number of decimal places for the number.
 * @param roundUp (optional) Whether to round up or just floor the value
 * past the given precision. Defaults to true.
 */
export function round(num: number, decimalPlaces = 0): number {
    const exp = 10 ** decimalPlaces;
    // Add the machine epsilon to ensure that numbers which are exactly halfway
    // between the two possible digits in the last decimal place are rounded correctly.
    // For example: 1.005 with 2 decimals should be rounded to 1.01. Without adding the
    // machine epsilon it would be rounded to: `Math.round(1.005 * 100) / 100 = 1`.
    // Adapted from: https://stackoverflow.com/questions/11832914/round-to-at-most-2-decimal-places-only-if-necessary
    return Math.round((num + Number.EPSILON) * exp) / exp;
}

/**
 * Maps a value using the `from` range to the `to` range.
 *
 * @example
 * mapValue(5, 0, 10, 0, 100) === 50
 * mapValue(5, 0, 10, 0, -100) === -50
 * mapValue(5, 0, 10, -50, 50) === 0
 */
export function mapValue(
    value: number,
    fromMin: number,
    fromMax: number,
    toMin: number,
    toMax: number,
): number {
    return (toMax - toMin) * ((value - fromMin) / (fromMax - fromMin)) + toMin;
}

export const clamp = (value: number, min: number, max: number) =>
    Math.min(Math.max(min, value), max);

/**
 * Generates a random number between the `min` and `max`.
 *
 * @param min the min.
 * @param max the max.
 * @param integer if the result should be floored to an integer.
 */
export const random = (min: number, max: number, integer = false) => {
    const finalMin = Math.min(min, max);
    const finalMax = Math.max(min, max);
    const range = finalMax - finalMin;
    const value = finalMin + Math.random() * range;
    return integer ? Math.floor(value) : value;
};
